import React, { useState } from 'react';
import {
  FileSpreadsheet,
  Search,
  Filter,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Edit3,
  Clock,
  Save,
  X,
  ShieldCheck,
  Building,
  Calendar,
  Layers,
  ChevronRight
} from 'lucide-react';
import { useGst } from '../context/GstContext';
import { ExtractedGstInvoice, InvoiceCategory, ItcEligibility } from '../types/gst';
import { validateAndCalculateInvoiceMath } from '../services/mathValidator';
import { toast } from '../components/ui';

export const LedgerPage: React.FC = () => {
  const { invoices, updateInvoice, taxPeriod, financialYear } = useGst();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedInvoice, setSelectedInvoice] = useState<ExtractedGstInvoice | null>(null);

  // Edit State
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<Partial<ExtractedGstInvoice>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Filter invoices
  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch =
      (inv.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.supplier_gstin || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === 'ALL' || inv.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const totalTaxable = filteredInvoices.reduce((acc, i) => acc + (i.total_taxable_value || 0), 0);
  const totalTax = filteredInvoices.reduce((acc, i) => acc + (i.total_tax || 0), 0);
  const mathIssuesCount = invoices.filter((i) => !i.math_verified).length;

  const handleRowClick = (inv: ExtractedGstInvoice) => {
    setSelectedInvoice(inv);
    setEditFormData(inv);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedInvoice) return;
    setIsSaving(true);
    try {
      // Deterministically recalculate math
      const math = validateAndCalculateInvoiceMath(editFormData);
      const updated: ExtractedGstInvoice = {
        ...selectedInvoice,
        ...editFormData,
        line_items: math.verifiedLineItems,
        total_taxable_value: math.totalTaxableValue,
        total_tax: math.totalTax,
        invoice_total: math.calculatedInvoiceTotal,
        math_verified: math.mathVerified,
        normalized_invoice_number: math.normalizedInvoiceNumber
      } as ExtractedGstInvoice;

      await updateInvoice(updated, 'MCA_AUDIT_MANUAL_ADJUSTMENT');
      setSelectedInvoice(updated);
      setIsEditing(false);
      toast.success(`Invoice ${updated.invoice_number} updated with MCA audit trail`);
    } catch (err: any) {
      console.error('Failed to update invoice:', err);
      toast.error('Failed to update invoice: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title & Summary Metrics */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-cyan-400" />
            <span>Interactive Accounting & Invoice Ledger</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            High-density spreadsheet matrix with deterministic line-item math and MCA audit trail compliance
          </p>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <div className="rounded-xl border border-white/10 bg-[#131316] px-3.5 py-2">
            <div className="text-[10px] uppercase font-bold text-slate-500">Total Invoices</div>
            <div className="text-sm font-bold text-white font-mono">{filteredInvoices.length}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#131316] px-3.5 py-2">
            <div className="text-[10px] uppercase font-bold text-slate-500">Taxable Value</div>
            <div className="text-sm font-bold text-cyan-300 font-mono">₹{totalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#131316] px-3.5 py-2">
            <div className="text-[10px] uppercase font-bold text-slate-500">Total Tax</div>
            <div className="text-sm font-bold text-emerald-400 font-mono">₹{totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          {mathIssuesCount > 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-3.5 py-2 text-amber-300">
              <div className="text-[10px] uppercase font-bold">Needs Review</div>
              <div className="text-sm font-bold font-mono">{mathIssuesCount} Mismatches</div>
            </div>
          )}
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#131316] p-3 rounded-2xl border border-white/10 text-xs">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search invoice number, supplier name, or GSTIN..."
            className="w-full bg-transparent text-white placeholder-slate-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto">
          {['ALL', 'B2B_SALES', 'B2B_PURCHASES', 'EXPENSE_RECEIPT'].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl font-medium transition ${
                selectedCategory === cat
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {cat === 'ALL' ? 'All' : cat.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Main Ledger Grid */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Invoice #</th>
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Category</th>
              <th className="py-3 px-4">Supplier / Party</th>
              <th className="py-3 px-4">Supplier GSTIN</th>
              <th className="py-3 px-4 text-right">Taxable (₹)</th>
              <th className="py-3 px-4 text-right">Tax (₹)</th>
              <th className="py-3 px-4 text-right">Total (₹)</th>
              <th className="py-3 px-4 text-center">Math Audit</th>
              <th className="py-3 px-4 text-center">ITC Eligibility</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-12 text-center text-slate-500 font-sans">
                  No invoices found in ledger for {taxPeriod} {financialYear}. Ingest files in the Upload Vault.
                </td>
              </tr>
            ) : (
              filteredInvoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => handleRowClick(inv)}
                  className="hover:bg-white/[0.03] cursor-pointer transition"
                >
                  <td className="py-3 px-4 font-bold text-white">{inv.invoice_number}</td>
                  <td className="py-3 px-4 text-slate-400">{inv.invoice_date}</td>
                  <td className="py-3 px-4">
                    <span className="rounded bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 border border-white/10">
                      {inv.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-sans font-medium text-slate-200 truncate max-w-[160px]">
                    {inv.supplier_name}
                  </td>
                  <td className="py-3 px-4 text-slate-400">{inv.supplier_gstin || 'N/A'}</td>
                  <td className="py-3 px-4 text-right text-slate-300">
                    {(inv.total_taxable_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-4 text-right text-cyan-300 font-bold">
                    {(inv.total_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-4 text-right text-white font-bold">
                    {(inv.invoice_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {inv.math_verified ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400 border border-emerald-500/20">
                        <CheckCircle className="h-3 w-3" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-400 border border-amber-500/20">
                        <AlertTriangle className="h-3 w-3" /> Check
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center font-sans">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                        inv.itc_eligibility === 'ELIGIBLE'
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : inv.itc_eligibility === 'BLOCKED_17_5'
                          ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                          : inv.itc_eligibility === 'RCM_APPLICABLE'
                          ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20'
                          : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {inv.itc_eligibility}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(inv);
                      }}
                      className="p-1 rounded text-cyan-400 hover:bg-cyan-500/10"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Side-by-Side Split View Drawer / Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="relative w-full max-w-5xl rounded-2xl border border-[rgba(247,247,248,0.14)] bg-[#131316] shadow-2xl flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 bg-[#0e1015]">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-cyan-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Invoice Audit & MCA Trail: #{selectedInvoice.invoice_number}
                  </h3>
                  <p className="text-xs text-slate-400">{selectedInvoice.supplier_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
                    isEditing
                      ? 'bg-cyan-500 text-black border-cyan-400'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10 border-white/10'
                  }`}
                >
                  {isEditing ? 'Cancel Edit' : 'Edit Fields'}
                </button>
                {isEditing && (
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs transition"
                  >
                    <Save className="h-3.5 w-3.5" />
                    <span>{isSaving ? 'Recalculating...' : 'Save & Audit'}</span>
                  </button>
                )}
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-slate-400 hover:text-white p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Split View Content */}
            <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {/* Left Pane: Original Source Document Info */}
              <div className="p-6 space-y-4 bg-[#0b0c0e]">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                  <span>Source Ingestion Artifact</span>
                  {selectedInvoice.source_file_url && (
                    <a
                      href={selectedInvoice.source_file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-cyan-400 hover:underline flex items-center gap-1 text-[11px] font-sans"
                    >
                      <ExternalLink className="h-3 w-3" /> View Original File
                    </a>
                  )}
                </h4>

                <div className="rounded-xl border border-white/10 bg-[#131316] p-4 text-xs space-y-2">
                  <div className="text-slate-400">Storage Path:</div>
                  <div className="font-mono text-cyan-300 break-all">{selectedInvoice.source_file_path}</div>
                  <div className="grid grid-cols-2 gap-2 pt-2 text-slate-400">
                    <div>Tax Period: <span className="text-white font-mono">{selectedInvoice.taxPeriod}</span></div>
                    <div>FY: <span className="text-white font-mono">{selectedInvoice.financialYear}</span></div>
                  </div>
                </div>

                {/* AI Compliance Note */}
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-950/20 p-4 text-xs space-y-1">
                  <div className="font-bold text-cyan-300 flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4" />
                    Statutory Compliance Assessment
                  </div>
                  <p className="text-slate-300 leading-relaxed text-[11px]">
                    {selectedInvoice.ai_compliance_note || 'Eligible under Section 16(2) CGST Act.'}
                  </p>
                </div>

                {/* MCA Immutable Audit Trail */}
                <div className="space-y-2 pt-2">
                  <h5 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-cyan-400" />
                    Immutable MCA Audit Trail ({selectedInvoice.audit_trail?.length || 0})
                  </h5>
                  <div className="space-y-2 max-h-48 overflow-y-auto font-mono text-[11px]">
                    {(selectedInvoice.audit_trail || []).map((trail, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-white/5 bg-[#18191f] p-2.5 space-y-1"
                      >
                        <div className="flex items-center justify-between text-cyan-400 font-bold">
                          <span>{trail.action}</span>
                          <span className="text-slate-500 text-[10px]">{trail.timestamp}</span>
                        </div>
                        <div className="text-slate-400 text-[10px]">{trail.details || 'System event'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Pane: Extracted Line Items & Form */}
              <div className="p-6 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Extracted Financial Fields
                </h4>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Invoice #</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={editFormData.invoice_number || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, invoice_number: e.target.value })}
                      className="w-full rounded-lg border border-white/10 bg-[#0e1015] px-2.5 py-1.5 font-mono text-white focus:outline-none focus:border-cyan-500 disabled:opacity-75"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Invoice Date</label>
                    <input
                      type="date"
                      disabled={!isEditing}
                      value={editFormData.invoice_date || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, invoice_date: e.target.value })}
                      className="w-full rounded-lg border border-white/10 bg-[#0e1015] px-2.5 py-1.5 font-mono text-white focus:outline-none focus:border-cyan-500 disabled:opacity-75"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Supplier GSTIN</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={editFormData.supplier_gstin || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, supplier_gstin: e.target.value.toUpperCase() })}
                      className="w-full rounded-lg border border-white/10 bg-[#0e1015] px-2.5 py-1.5 font-mono text-white focus:outline-none focus:border-cyan-500 disabled:opacity-75 uppercase"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Supplier Name</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={editFormData.supplier_name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, supplier_name: e.target.value })}
                      className="w-full rounded-lg border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white focus:outline-none focus:border-cyan-500 disabled:opacity-75"
                    />
                  </div>
                </div>

                {/* Line Items Table */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-white">Line Items ({(editFormData.line_items || []).length})</span>
                    <span className="text-slate-400 text-[11px]">Strict Decimal.js Math Verified</span>
                  </div>

                  <div className="rounded-xl border border-white/10 overflow-hidden text-xs font-mono">
                    <table className="w-full text-left">
                      <thead className="bg-[#0e1015] text-[10px] text-slate-400 uppercase">
                        <tr>
                          <th className="p-2">Description</th>
                          <th className="p-2">HSN/SAC</th>
                          <th className="p-2 text-right">Taxable (₹)</th>
                          <th className="p-2 text-right">Rate (%)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {(editFormData.line_items || []).map((line, idx) => (
                          <tr key={idx} className="hover:bg-white/[0.02]">
                            <td className="p-2 font-sans truncate max-w-[140px] text-slate-200">
                              {line.description}
                            </td>
                            <td className="p-2 text-cyan-400">{line.hsn_sac_code || '9983'}</td>
                            <td className="p-2 text-right text-slate-200">
                              {line.taxable_value.toFixed(2)}
                            </td>
                            <td className="p-2 text-right text-slate-400">
                              {(line.cgst_rate || 0) + (line.sgst_rate || 0) + (line.igst_rate || 0)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="rounded-xl border border-white/10 bg-[#0e1015] p-3 text-xs space-y-1 font-mono">
                  <div className="flex justify-between text-slate-400">
                    <span>Taxable Amount:</span>
                    <span>₹{(selectedInvoice.total_taxable_value || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-cyan-300">
                    <span>Computed Total Tax:</span>
                    <span>₹{(selectedInvoice.total_tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-white text-sm pt-1 border-t border-white/10">
                    <span>Invoice Grand Total:</span>
                    <span>₹{(selectedInvoice.invoice_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
